import { useState, useRef, useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { ChevronDown, User, Package, LogOut, LayoutDashboard } from 'lucide-react';
import { useAuth } from '@core/auth/hooks/useAuth';
import { Role } from '@core/auth/types/auth.types';
import { USER_MENU_ITEMS } from '@shared/constants/navigation';
import { ROUTES } from '@shared/constants/routes';

const iconMap = {
  User,
  Package,
  LogOut,
  LayoutDashboard,
} as const;

/**
 * Returns the dashboard href based on user role
 */
function getDashboardHref(role: Role): string | null {
  if (role === Role.ADMIN) return ROUTES.ADMIN.DASHBOARD;
  if (role === Role.VENDOR) return ROUTES.VENDOR.DASHBOARD;
  return null;
}

export function UserMenu() {
  const { user, logout } = useAuth();
  const [isOpen, setIsOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setIsOpen(false);
      }
    };

    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, []);

  const handleLogout = () => {
    setIsOpen(false);
    logout();
  };

  // Filter menu items by user role and map Dashboard href dynamically
  const visibleItems = useMemo(() => {
    if (!user) return [];

    return USER_MENU_ITEMS
      .filter((item) => {
        // If no roles restriction, show to everyone
        if (!item.roles) return true;
        // If roles specified, check if user has one of them
        return item.roles.includes(user.role);
      })
      .map((item) => {
        // Override Dashboard href based on user role
        if (item.label === 'Dashboard') {
          const dashboardHref = getDashboardHref(user.role);
          return { ...item, href: dashboardHref || item.href };
        }
        return item;
      });
  }, [user]);

  return (
    <div className="relative" ref={menuRef}>
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center gap-2 text-brand-carbon hover:text-brand-mauve transition-colors"
        aria-expanded={isOpen}
        aria-haspopup="true"
        aria-label="Abrir menú de usuario"
      >
        <div className="flex h-8 w-8 items-center justify-center rounded-full bg-brand-mauve text-white text-sm font-medium">
          {user?.fullName?.charAt(0).toUpperCase() || 'U'}
        </div>
        <span className="hidden sm:inline text-sm font-medium">
          {user?.fullName || 'Usuario'}
        </span>
        <ChevronDown
          className={`h-4 w-4 transition-transform ${isOpen ? 'rotate-180' : ''}`}
        />
      </button>

      {isOpen && (
        <div
          className="absolute right-0 mt-2 w-48 rounded-md bg-white py-1 shadow-lg ring-1 ring-black ring-opacity-5 z-50"
          role="menu"
          aria-orientation="vertical"
        >
          {visibleItems.map((item) => {
            const IconComponent = item.icon
              ? iconMap[item.icon as keyof typeof iconMap]
              : null;

            if (item.action === 'logout') {
              return (
                <button
                  key="logout"
                  onClick={handleLogout}
                  className="flex w-full items-center gap-2 px-4 py-2 text-sm text-brand-carbon hover:bg-brand-smoke transition-colors"
                  role="menuitem"
                >
                  {IconComponent && <IconComponent className="h-4 w-4" />}
                  {item.label}
                </button>
              );
            }

            return (
              <Link
                key={item.href}
                to={item.href!}
                onClick={() => setIsOpen(false)}
                className="flex items-center gap-2 px-4 py-2 text-sm text-brand-carbon hover:bg-brand-smoke transition-colors"
                role="menuitem"
              >
                {IconComponent && <IconComponent className="h-4 w-4" />}
                {item.label}
              </Link>
            );
          })}
        </div>
      )}
    </div>
  );
}
